import {NonEmptyRecord} from '../types';
import {kindOf} from './kindOf';

export type OmitEmptyOptions = {
  omitZero?: boolean;
  omitFalse?: boolean;
  omitNull?: boolean;
  omitUndefined?: boolean;
  omitEmptyString?: boolean;
  omitEmptyArray?: boolean;
  omitEmptyObject?: boolean;
};

const defaultEmptyOptions: OmitEmptyOptions = {
  omitZero: false,
  omitFalse: false,
  omitNull: true,
  omitUndefined: true,
  omitEmptyString: true,
  omitEmptyArray: true,
  omitEmptyObject: true
};

export const isEmpty = (value?: any, options: OmitEmptyOptions = {}) => {
  options = {...defaultEmptyOptions, ...options};
  switch (kindOf(value)) {
    case 'null':
      return options.omitNull;
    case 'undefined':
      return options.omitUndefined;
    case 'boolean':
    case 'function':
    case 'date':
    case 'regexp':
      return false;
    case 'string':
      return options.omitEmptyString && value === '';
    case 'arguments':
      return value.length === 0;
    case 'map':
    case 'set':
      return value.size === 0;
    case 'number':
      return options.omitZero && value === 0;
    case 'error':
      return value.message === '';
    case 'array':
      if (value.length === 0) {
        return options.omitEmptyArray;
      }
      for (let ele of value) {
        if (!isEmpty(ele, options)) {
          return false;
        }
      }
      return true;
    case 'object':
      if (Object.keys(value).length === 0) {
        return options.omitEmptyObject;
      }
      for (let key of Object.keys(value)) {
        if (!isEmpty(value[key], options)) {
          return false;
        }
      }
      return true;
    default: {
      return true;
    }
  }
};

export const omitEmpty = <T extends Record<any, any>, V = NonEmptyRecord<T>>(
  obj: T,
  options: OmitEmptyOptions = {}
): V => {
  options = {...defaultEmptyOptions, ...options};
  let omit = (value: any) => {
    if (Array.isArray(value)) {
      value = value.map((v) => omit(v)).filter((v) => !isEmpty(v, options));
    }

    if (kindOf(value) === 'object') {
      let result: any = {};
      for (let key of Object.keys(value)) {
        let val = omit(value[key]);
        if (val !== void 0) {
          result[key] = val;
        }
      }
      value = result;
    }

    if (!isEmpty(value, options)) {
      return value;
    }
  };

  let res = omit(obj);
  if (res === void 0) {
    return kindOf(obj) === 'object' ? {} : res;
  }
  return res;
};
